/**
 * Størrelsesguide: step-by-step (height -> weight -> usual size ->
 * [fit preference, clothing only] -> result).
 * Clothing recommendation nudges the shopper's stated usual size from a
 * simple BMI signal, then shifts further based on desired fit: "stramt"
 * (fitted) keeps that size, "normal" sizes up by one, "oversized" sizes
 * up by two. Shoe size has no reliable height/weight formula and no
 * fit-preference step, so it confirms the shopper's stated usual size.
 * Clothing sizes come from the product itself (data-mc-sg-sizes, smallest
 * first), so the recommendation stays within the sizes that model offers.
 * The fit keys stay language-neutral — they are internal values, never
 * shown to the shopper.
 */
(function () {
  'use strict';

  var DEFAULT_CLOTHING_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
  var FIT_OFFSETS = { stramt: 0, normal: 1, oversized: 2 };

  document.querySelectorAll('[data-mc-sg]').forEach(function (root) {
    var isShoe = root.getAttribute('data-mc-sg-shoe') === 'true';
    var sizesAttr = root.getAttribute('data-mc-sg-sizes');
    var clothingSizes = sizesAttr ? sizesAttr.split(',') : DEFAULT_CLOTHING_SIZES;
    var totalSteps = parseInt(root.getAttribute('data-mc-sg-total-steps'), 10) || (isShoe ? 4 : 5);
    var overlay = root.querySelector('[data-mc-sg-overlay]');
    var openBtn = root.querySelector('[data-mc-sg-open]');
    var closeBtns = root.querySelectorAll('[data-mc-sg-close]');
    var restartBtn = root.querySelector('[data-mc-sg-restart]');
    var nextBtns = root.querySelectorAll('[data-mc-sg-next]');
    var backBtns = root.querySelectorAll('[data-mc-sg-back]');
    var resultBtns = root.querySelectorAll('[data-mc-sg-result]');
    var steps = root.querySelectorAll('[data-mc-sg-step]');
    var dots = root.querySelectorAll('[data-mc-sg-dot]');
    var heightInput = root.querySelector('[data-mc-sg-height]');
    var weightInput = root.querySelector('[data-mc-sg-weight]');
    var choiceBtns = root.querySelectorAll('[data-mc-sg-choice]');
    var fitBtns = root.querySelectorAll('[data-mc-sg-fit]');
    var resultValueEl = root.querySelector('[data-mc-sg-result-value]');
    var resultNoteEl = root.querySelector('[data-mc-sg-result-note]');

    var state = { step: 1, height: null, weight: null, usualSize: null, fit: null };

    function showStep(n) {
      state.step = n;
      steps.forEach(function (s) {
        s.hidden = parseInt(s.getAttribute('data-mc-sg-step'), 10) !== n;
      });
      dots.forEach(function (d) {
        var idx = parseInt(d.getAttribute('data-mc-sg-dot'), 10);
        d.classList.toggle('is-active', idx === n);
        d.classList.toggle('is-done', idx < n);
      });
    }

    function open() {
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      showStep(1);
    }

    function close() {
      overlay.hidden = true;
      document.body.style.overflow = '';
    }

    function reset() {
      state = { step: 1, height: null, weight: null, usualSize: null, fit: null };
      if (heightInput) heightInput.value = '';
      if (weightInput) weightInput.value = '';
      choiceBtns.forEach(function (b) { b.classList.remove('is-selected'); });
      fitBtns.forEach(function (b) { b.classList.remove('is-selected'); });
      showStep(1);
    }

    if (openBtn) openBtn.addEventListener('click', open);
    closeBtns.forEach(function (b) { b.addEventListener('click', close); });
    if (restartBtn) restartBtn.addEventListener('click', reset);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hidden) close();
    });

    nextBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (state.step === 1) {
          var h = parseInt(heightInput.value, 10);
          if (!h || h < 100 || h > 250) {
            heightInput.focus();
            return;
          }
          state.height = h;
          showStep(2);
        } else if (state.step === 2) {
          var w = parseInt(weightInput.value, 10);
          if (!w || w < 20 || w > 300) {
            weightInput.focus();
            return;
          }
          state.weight = w;
          showStep(3);
        } else if (state.step === 3) {
          if (!state.usualSize) return;
          showStep(4);
        }
      });
    });

    backBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        showStep(Math.max(1, state.step - 1));
      });
    });

    choiceBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        choiceBtns.forEach(function (b) { b.classList.remove('is-selected'); });
        btn.classList.add('is-selected');
        state.usualSize = btn.getAttribute('data-mc-sg-choice');
      });
    });

    fitBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        fitBtns.forEach(function (b) { b.classList.remove('is-selected'); });
        btn.classList.add('is-selected');
        state.fit = btn.getAttribute('data-mc-sg-fit');
      });
    });

    function recommendClothing() {
      var idx = clothingSizes.indexOf(state.usualSize);
      if (idx === -1) return state.usualSize;
      var maxIdx = clothingSizes.length - 1;
      var heightM = state.height / 100;
      var bmi = state.weight / (heightM * heightM);
      var bmiNote;
      if (bmi < 18.5 && idx > 0) {
        idx -= 1;
        bmiNote = "D'après votre taille et votre poids, une taille en dessous de votre taille habituelle pourrait mieux convenir. ";
      } else if (bmi >= 30 && idx < maxIdx) {
        idx += 1;
        bmiNote = "D'après votre taille et votre poids, une taille au-dessus de votre taille habituelle offrira une coupe plus confortable. ";
      } else {
        bmiNote = 'Votre taille habituelle convient bien à votre taille et à votre poids. ';
      }

      var fitOffset = FIT_OFFSETS[state.fit] || 0;
      var capped = idx + fitOffset > maxIdx;
      idx = Math.min(idx + fitOffset, maxIdx);

      var fitNote;
      if (state.fit === 'stramt') {
        fitNote = 'Pour un look ajusté, nous avons conservé la taille telle quelle.';
      } else if (state.fit === 'oversized') {
        fitNote = 'Pour un look oversize, nous avons choisi deux tailles au-dessus.';
      } else {
        fitNote = 'Pour un look normal, nous avons choisi une taille au-dessus.';
      }
      if (capped) {
        fitNote += " C'est la plus grande taille disponible pour ce modèle.";
      }

      resultNoteEl.textContent = bmiNote + fitNote;
      return clothingSizes[idx];
    }

    function recommendShoe() {
      resultNoteEl.textContent = 'La pointure dépend de la longueur et de la largeur du pied, et non de la taille ou du poids — nous vous recommandons donc la pointure que vous portez habituellement.';
      return state.usualSize;
    }

    resultBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!state.usualSize) return;
        if (!isShoe && !state.fit) return;
        var size = isShoe ? recommendShoe() : recommendClothing();
        resultValueEl.textContent = size;
        showStep(totalSteps);
      });
    });
  });
})();